import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { api } from '@/utils/api';

import { NotifType } from './FreelancersNotifications';

const Box = styled.div`
  padding: 8px 34px 8px 16px;
  cursor: pointer;
  position: relative;
  transition: background-color ease-out 0.2s;
  &:hover {
    background-color: ${({ theme }) => theme.colors.grayscaleSuperLight};
  }
`;

const StyledText = styled(Text)`
  margin-bottom: 2px;

  strong {
    font-weight: 700;
  }
`;

const NotifDot = styled.div`
  height: 6px;
  width: 6px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.success};
  position: absolute;
  top: calc(50% - 3px);
  right: 16px;
`;

interface Props {
  type: NotifType;
  notification: {
    id: string;
    readAt?: string;
    data: {
      freelancer_id: string;
      signed_at?: string;
      signer_name?: string;
      freelancer_name?: string;
      requested_at?: string;
    };
  };
  inCenter?: boolean;
}

const Notification: FC<Props> = ({ type, notification, inCenter }) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const handleClick = async () => {
    try {
      await api.put(`/v1/notifications/${notification.id}/read`);

      const queryKey =
        type === NotifType.Documents ? 'document_signed' : 'vacation_requested';
      queryClient.invalidateQueries([queryKey], {
        exact: false,
      });

      navigate(`/general/freelancers/${notification.data.freelancer_id}`);
    } catch (err) {
      alerts.error();
    }
  };

  const text =
    type === NotifType.Documents
      ? 'recently signed documents in DocuSign'
      : 'recently requested vacation';

  const name =
    type === NotifType.Documents
      ? notification.data.signer_name
      : notification.data.freelancer_name;

  const dateField =
    type === NotifType.Documents
      ? notification.data.signed_at
      : notification.data.requested_at;

  const dt = DateTime.fromSQL(dateField || '', { zone: 'local' });
  const prettyDate = dt.toFormat('MMMM d, yyyy · hh:mm a');

  return (
    <Box onClick={handleClick}>
      <StyledText $color="black" $type="smallText">
        <strong>{name}</strong> {text}
      </StyledText>
      <Text $color="gray" $type="smallNormal">
        {prettyDate}
      </Text>
      {!notification.readAt && <NotifDot />}
    </Box>
  );
};

export default Notification;
